import { Box } from "@mantine/core";
import { DateTime } from "luxon";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { useEffect, useRef, useState } from "react";
import { DateInput } from "../../../../mantine/dates/DateInput/DateInput";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import { getRowId } from "../hooks/usePeriodBasedTable";
import { TableCellErrorText } from "../PeriodBasedTable";
import { EMPTY_DATE_PLACEHOLDER } from "../PeriodBasedTable.constants";
import type {
  ExternalErrors,
  PeriodBasedRow,
  ValidationErrors
} from "../PeriodBasedTable.types";

interface FirstDateColumnConfig {
  rows: PeriodBasedRow[];
  originalRows: PeriodBasedRow[];
  creationErrors?: ValidationErrors;
  editingErrors?: ValidationErrors;
  externalErrors?: ExternalErrors;
  onUpdatePreviousRowLastDate?: (
    previousRowId: string,
    newLastDate: string,
    isTemporaryDate?: boolean
  ) => void;
}

/** The previous row ends the day before, so it has to keep at least one day. */
export function getFirstDateMinDate(
  previousRow: PeriodBasedRow | undefined
): DateTime | undefined {
  if (!previousRow?.first_date) {
    return undefined;
  }
  return backendDateOrDateTimeToLuxonDateTime(previousRow.first_date).plus({
    days: 1
  });
}

/** The row itself has to keep at least one day. */
export function getFirstDateMaxDate(
  lastDate: string | null | undefined
): DateTime | undefined {
  if (!lastDate) {
    return undefined;
  }
  return backendDateOrDateTimeToLuxonDateTime(lastDate);
}

function getDefaultFirstDate(lastRow: PeriodBasedRow): DateTime | null {
  if (lastRow.last_date) {
    const lastDate = backendDateOrDateTimeToLuxonDateTime(lastRow.last_date);
    if (lastDate) {
      return lastDate.plus({ days: 1 });
    }
  } else {
    const firstDate = backendDateOrDateTimeToLuxonDateTime(lastRow.first_date);
    if (firstDate) {
      const nextDay = firstDate.plus({ days: 1 });
      const today = DateTime.now().startOf("day");

      if (nextDay < today) {
        return today;
      }

      return nextDay;
    }
    return DateTime.now();
  }
  return null;
}

export function getFirstDateColumn({
  rows,
  originalRows,
  creationErrors,
  editingErrors,
  externalErrors,
  onUpdatePreviousRowLastDate
}: FirstDateColumnConfig): MRT_ColumnDef<PeriodBasedRow> {
  return {
    header: "Von",
    accessorKey: "first_date",
    size: 120,
    enableSorting: false,
    Edit: ({ cell, row, table }) => {
      const isCreatingRow = table.getState().creatingRow?.id === row.id;
      const isEditingRow = table.getState().editingRow?.id === row.id;
      const isTemporaryDate = isCreatingRow || isEditingRow;

      const cachedValue = row._valuesCache.first_date as
        string | null | undefined;
      const initialValue =
        cachedValue !== undefined
          ? cachedValue
          : (cell.getValue() as string | null);

      const [value, setValue] = useState<DateTime | null>(
        backendDateOrDateTimeToLuxonDateTimeOrNull(initialValue)
      );
      const hasInitializedRef = useRef(false);

      useEffect(() => {
        row._valuesCache.first_date = value
          ? luxonDateTimeToBackendDateOrDateTime(value, "ISO 8601")
          : null;
      }, [value, row]);

      const shouldInitializeFirstDate =
        isCreatingRow && !row._valuesCache.first_date && rows.length > 0;
      const previousRow = isCreatingRow
        ? rows[rows.length - 1]
        : rows[row.index - 1];

      if (shouldInitializeFirstDate) {
        const defaultDate = getDefaultFirstDate(previousRow);
        if (defaultDate) {
          row._valuesCache.first_date = luxonDateTimeToBackendDateOrDateTime(
            defaultDate,
            "ISO 8601"
          );
          if (!value) {
            setValue(defaultDate);
          }
        }
      }

      useEffect(() => {
        if (
          isCreatingRow &&
          value &&
          !hasInitializedRef.current &&
          onUpdatePreviousRowLastDate &&
          previousRow
        ) {
          if (value) {
            const newLastDate = luxonDateTimeToBackendDateOrDateTime(
              value.minus({ days: 1 })
            );
            const originalPreviousRow = originalRows[originalRows.length - 1];
            const previousRowId = originalPreviousRow
              ? getRowId(originalPreviousRow)
              : getRowId(previousRow);

            onUpdatePreviousRowLastDate(previousRowId, newLastDate, true);
            hasInitializedRef.current = true;
          }
        }
      }, [isCreatingRow, value, previousRow]);

      const rowId = getRowId(row.original);
      const externalError =
        !isCreatingRow && !isEditingRow && externalErrors?.[rowId]?.first_date;
      const errorText =
        externalError ||
        (isCreatingRow
          ? creationErrors?.firstDate
          : isEditingRow
            ? editingErrors?.firstDate
            : undefined);

      const handleChange = (newValue: DateTime | null) => {
        setValue(newValue);

        if (newValue && onUpdatePreviousRowLastDate) {
          if (newValue) {
            const newLastDate = luxonDateTimeToBackendDateOrDateTime(
              newValue.minus({ days: 1 })
            );

            if (previousRow) {
              const originalPreviousRow = isCreatingRow
                ? originalRows[originalRows.length - 1]
                : originalRows[row.index - 1];
              const previousRowId = originalPreviousRow
                ? getRowId(originalPreviousRow)
                : getRowId(previousRow);

              onUpdatePreviousRowLastDate(
                previousRowId,
                newLastDate,
                isTemporaryDate
              );

              const editingRow = table.getState().editingRow;
              if (editingRow) {
                const editingRowData = rows[editingRow.index];
                if (editingRowData && editingRowData === previousRow) {
                  editingRow._valuesCache.last_date = newLastDate;
                }
              }
            }
          }
        }

        const { editingRow, creatingRow } = table.getState();
        if (creatingRow?.id === row.id) {
          table.setCreatingRow(row);
        } else if (editingRow?.id === row.id) {
          table.setEditingRow(row);
        }
      };

      return (
        <DateInput
          aria-label="Von"
          clearable={false}
          error={errorText || undefined}
          maxDate={getFirstDateMaxDate(
            (row._valuesCache.last_date as string | null | undefined) ??
              row.original.last_date
          )}
          minDate={getFirstDateMinDate(previousRow)}
          placeholder="Startdatum"
          value={value}
          onChange={handleChange}
        />
      );
    },
    Cell: ({ row }) => {
      const rowId = getRowId(row.original);
      const externalError = externalErrors?.[rowId]?.first_date;
      return (
        <Box>
          {backendDateOrDateTimeToLuxonDateTimeOrNull(
            row.original.first_date
          )?.toLocaleString({
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
          }) || EMPTY_DATE_PLACEHOLDER}
          <TableCellErrorText error={externalError || undefined} />
        </Box>
      );
    }
  };
}
